import Eyebrow from '@Guidelines/Component/Eyebrow';
import ConditionalLoader from '@Guidelines/Component/Loader/ConditionalLoader';
import { theme } from '@Guidelines/Theme/theme';
import type SubjectTypeGrantsFormData from '@UI/Pages/Administration/SubjectTypes/Shared/FormData/SubjectTypeGrantsFormData';
import type { GrantDraft } from '@UI/Pages/Administration/SubjectTypes/Shared/FormData/SubjectTypeGrantsFormData';
import { css } from '@emotion/react';
import * as React from 'react';
import { Grid, GridItem } from '@verclam/grid-system';
import { useSelector } from '@verclam/synapse';
import { Button, Dropdown } from '@verclam/ui-kit';

/** The theme gives every kit field a 1.25rem side margin: text outside a field starts where the fields do. */
const indentStyle = css`
	margin-inline-start: 1.25rem;
`;

const nameStyle = css`
	font-family: ${theme.fonts.body};
	font-size: 0.90625rem;
	font-weight: ${theme.fontWeights.semibold};
	color: ${theme.colors.ink};
	margin-inline-start: 1.25rem;
`;

type Props = {
	readonly grants: SubjectTypeGrantsFormData;
	readonly onChange: (update: (grants: SubjectTypeGrantsFormData) => SubjectTypeGrantsFormData) => void;
	readonly index: number;
	readonly grant: GrantDraft;
	/** The resolved name of the role or user, or the sentence saying it is unknown. */
	readonly name: string;
	readonly isReadOnly: boolean;
};

const KIND_LABELS: Readonly<Record<GrantDraft['kind'], string>> = {
	role: 'Rôle',
	user: 'Utilisateur',
};

/** One access line: who it covers, at which level, and the button removing it. */
const GrantRow: React.FC<Props> = ({ grants, onChange, index, grant, name, isReadOnly }) => {
	const accessLevels = useSelector((state) => state.subjectTypeDefinitionsStore.accessLevels);
	const serverMessage = grants.getGrantServerMessage(index);

	const handleLevelChange = React.useCallback(
		(value: string | null) => {
			if (value !== null) {
				onChange((current) => current.setGrantLevel(index, value));
			}
		},
		[onChange, index],
	);

	const handleRemoveClick = React.useCallback(() => {
		onChange((current) => current.removeGrant(index));
	}, [onChange, index]);

	return (
		<div>
			<Grid columns={12} columnGap={1} alignItems="center">
				<GridItem columnSize={{ xs: 12, xl: 2 }}>
					<div css={indentStyle}>
						<Eyebrow>{KIND_LABELS[grant.kind]}</Eyebrow>
					</div>
				</GridItem>
				<GridItem columnSize={{ xs: 12, xl: 4 }}>
					<span css={nameStyle}>{name}</span>
				</GridItem>
				<GridItem columnSize={{ xs: 8, xl: 4 }}>
					<Dropdown
						name={`grantLevel${index}`}
						label={`Niveau d'accès de ${name}`}
						isLabelHidden
						options={accessLevels ?? []}
						optionLabel="label"
						optionValue="value"
						value={grant.level}
						disabled={isReadOnly}
						onChange={handleLevelChange}
					/>
				</GridItem>
				<GridItem columnSize={{ xs: 4, xl: 2 }}>
					<Button
						type="button"
						size="sm"
						severity="secondary"
						label="Retirer"
						disabled={isReadOnly}
						onClick={handleRemoveClick}
					/>
				</GridItem>
			</Grid>
			<ConditionalLoader data={serverMessage}>
				{(message) => <small className="ui-kit-field-error">{message}</small>}
			</ConditionalLoader>
		</div>
	);
};

export default GrantRow;
